import { useId } from "react";

/**
 * A sparkline, drawn by hand rather than by a charting library.
 *
 * Recharts would do this, and pulling it in for a 96×28 polyline would cost
 * ~90 kB gzipped to render eight elements — on a console whose whole point
 * is that it stays cheap to run. The two shapes below are the entire chart
 * vocabulary this app needs, and they are 60 lines.
 *
 * Two deliberate choices in the drawing:
 *
 *   1. **The last point is emphasised.** A filled dot with a ring in the
 *      panel's own background colour, so it separates from the line without
 *      a stroke. On a trend chart the reader is almost always asking "where
 *      is it NOW", and the eye should land there first.
 *
 *   2. **A flat series still draws flat.** With one distinct value the naive
 *      normalisation divides by zero and every point lands on the top edge,
 *      which reads as a spike. Zero range pins the line to the middle, which
 *      is what "nothing happened" should look like.
 */
export function Sparkline({
  values,
  width = 104,
  height = 28,
  tone = "accent",
  className,
}: {
  values: number[];
  width?: number;
  height?: number;
  tone?: "accent" | "muted";
  className?: string;
}) {
  const gradientId = useId();
  if (values.length < 2) {
    return <div style={{ width, height }} className={className} />;
  }

  const pad = 3;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min;
  const stepX = (width - pad * 2) / (values.length - 1);
  const y = (value: number) =>
    range === 0
      ? height / 2
      : height - pad - ((value - min) / range) * (height - pad * 2);

  const points = values.map((v, i) => [pad + i * stepX, y(v)] as const);
  const line = points.map(([px, py]) => `${px},${py}`).join(" ");
  const area = `${pad},${height} ${line} ${width - pad},${height}`;
  const last = points[points.length - 1] ?? [0, 0];
  const stroke =
    tone === "accent"
      ? "rgb(var(--color-brand-green))"
      : "rgb(var(--color-muted-2))";

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gradientId})`} />
      <polyline
        points={line}
        fill="none"
        stroke={stroke}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={last[0]}
        cy={last[1]}
        r="2.8"
        fill={stroke}
        stroke="rgb(var(--color-surface))"
        strokeWidth="1.5"
      />
    </svg>
  );
}
